import { useStore } from '../store/useStore.js';
import { uid } from '../utils/constants.js';
import type { GraphNode, GraphLink } from '@synapse/engine';

interface Template {
  id: string;
  name: string;
  description: string;
  emoji: string;
  build: (cx: number, cy: number) => { nodes: Partial<GraphNode>[]; links: Array<{ source: string; target: string }> };
}

const TEMPLATES: Template[] = [
  {
    id: 'brainstorm',
    name: 'Brainstorm',
    description: '1 tema central + 6 ideias ao redor',
    emoji: '🧠',
    build: (cx, cy) => {
      const centerId = uid();
      const ideas = Array.from({ length: 6 }, (_, i) => {
        const angle = (i / 6) * Math.PI * 2 - Math.PI / 2;
        return {
          id: uid(),
          label: `Ideia ${i + 1}`,
          color: ['#00f5ff', '#bf00ff', '#00ff88', '#ff6b35', '#ff0080', '#ffcc00'][i]!,
          x: cx + Math.cos(angle) * 220,
          y: cy + Math.sin(angle) * 220,
          type: 'idea' as const, category: 'idea' as const, size: 22,
        };
      });
      return {
        nodes: [
          { id: centerId, label: 'Tema Central', color: '#00f5ff', x: cx, y: cy, size: 34, type: 'universe', category: 'idea' },
          ...ideas,
        ],
        links: ideas.map((n) => ({ source: centerId, target: n.id! })),
      };
    },
  },
  {
    id: 'roadmap',
    name: 'Roadmap',
    description: '4 fases em sequência linear',
    emoji: '🗺️',
    build: (cx, cy) => {
      const phases = ['Q1: Planejamento', 'Q2: Desenvolvimento', 'Q3: Lançamento', 'Q4: Crescimento'];
      const colors = ['#00f5ff', '#00ff88', '#ffcc00', '#ff6b35'];
      const nodes = phases.map((label, i) => ({
        id: uid(), label, color: colors[i]!, type: 'idea' as const, category: 'strategy' as const, size: 26,
        x: cx + (i - 1.5) * 250, y: cy,
      }));
      return {
        nodes,
        links: nodes.slice(1).map((n, i) => ({ source: nodes[i]!.id!, target: n.id! })),
      };
    },
  },
  {
    id: 'pros-cons',
    name: 'Decisão Pros/Contras',
    description: 'Centro + coluna de Prós e Contras',
    emoji: '⚖️',
    build: (cx, cy) => {
      const decId = uid();
      const prosId = uid();
      const consId = uid();
      const proNodes = Array.from({ length: 3 }, (_, i) => ({
        id: uid(), label: `Pró ${i + 1}`, color: '#00ff88', type: 'idea' as const, category: 'idea' as const, size: 20,
        x: cx - 280, y: cy + (i - 1) * 140,
      }));
      const conNodes = Array.from({ length: 3 }, (_, i) => ({
        id: uid(), label: `Contra ${i + 1}`, color: '#f87171', type: 'idea' as const, category: 'execution' as const, size: 20,
        x: cx + 280, y: cy + (i - 1) * 140,
      }));
      return {
        nodes: [
          { id: decId, label: 'Decisão', color: '#00f5ff', x: cx, y: cy, size: 32, type: 'universe', category: 'strategy' },
          { id: prosId, label: 'Prós', color: '#00ff88', x: cx - 160, y: cy, size: 26, type: 'idea', category: 'idea' },
          { id: consId, label: 'Contras', color: '#f87171', x: cx + 160, y: cy, size: 26, type: 'idea', category: 'execution' },
          ...proNodes, ...conNodes,
        ],
        links: [
          { source: decId, target: prosId },
          { source: decId, target: consId },
          ...proNodes.map((n) => ({ source: prosId, target: n.id! })),
          ...conNodes.map((n) => ({ source: consId, target: n.id! })),
        ],
      };
    },
  },
  {
    id: 'mind-map',
    name: 'Mapa Mental',
    description: '5 ramos principais com sub-tópicos',
    emoji: '🌐',
    build: (cx, cy) => {
      const centerId = uid();
      const branches = Array.from({ length: 5 }, (_, i) => {
        const angle = (i / 5) * Math.PI * 2 - Math.PI / 2;
        const colors = ['#00f5ff', '#bf00ff', '#00ff88', '#ff6b35', '#ffcc00'];
        const brId = uid();
        const sub1 = { id: uid(), label: `${i + 1}.1`, color: colors[i]!, type: 'idea' as const, category: 'idea' as const, size: 18,
          x: cx + Math.cos(angle) * 340 + Math.cos(angle + 0.4) * 120,
          y: cy + Math.sin(angle) * 340 + Math.sin(angle + 0.4) * 120,
        };
        const sub2 = { id: uid(), label: `${i + 1}.2`, color: colors[i]!, type: 'idea' as const, category: 'idea' as const, size: 18,
          x: cx + Math.cos(angle) * 340 + Math.cos(angle - 0.4) * 120,
          y: cy + Math.sin(angle) * 340 + Math.sin(angle - 0.4) * 120,
        };
        return {
          branch: { id: brId, label: `Ramo ${i + 1}`, color: colors[i]!, type: 'idea' as const, category: 'idea' as const, size: 24,
            x: cx + Math.cos(angle) * 240, y: cy + Math.sin(angle) * 240 },
          subs: [sub1, sub2],
          brId,
        };
      });
      const nodes: Partial<GraphNode>[] = [
        { id: centerId, label: 'Ideia Principal', color: '#00f5ff', x: cx, y: cy, size: 36, type: 'universe', category: 'strategy' },
        ...branches.map((b) => b.branch),
        ...branches.flatMap((b) => b.subs),
      ];
      const links = [
        ...branches.map((b) => ({ source: centerId, target: b.brId })),
        ...branches.flatMap((b) => b.subs.map((s) => ({ source: b.brId, target: s.id! }))),
      ];
      return { nodes, links };
    },
  },
];

interface Props {
  open: boolean;
  onClose: () => void;
}

export function TemplatesModal({ open, onClose }: Props) {
  const addNode = useStore((s) => s.addNode);
  const createLink = useStore((s) => s.createLink);

  if (!open) return null;

  function apply(tpl: Template) {
    const cam = useStore.getState().cam;
    const cx = -cam.x;
    const cy = -cam.y;
    const { nodes, links } = tpl.build(cx, cy);

    useStore.getState().snapshot();
    const idMap = new Map<string, string>();

    for (const n of nodes) {
      const newId = addNode({ ...n, id: undefined });
      if (n.id) idMap.set(n.id, newId);
    }
    for (const l of links) {
      const src = idMap.get(l.source);
      const tgt = idMap.get(l.target);
      if (src && tgt) createLink(src, tgt, 'synapse');
    }
    onClose();
  }

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 390, background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(6px)' }} />
      <div
        className="glass-panel"
        style={{
          position: 'fixed',
          top: '50%', left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 520, maxWidth: '92vw',
          borderRadius: 16, padding: 24,
          zIndex: 400,
          boxShadow: '0 16px 64px rgba(0,0,0,0.7)',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <div>
            <h2 style={{ fontSize: 18, fontWeight: 700, marginBottom: 4 }}>Templates</h2>
            <p style={{ fontSize: 12, color: 'var(--muted)' }}>Escolha um ponto de partida para seu workspace</p>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--muted)', cursor: 'pointer', fontSize: 20, lineHeight: 1 }}>×</button>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          {TEMPLATES.map((tpl) => (
            <button
              key={tpl.id}
              onClick={() => apply(tpl)}
              style={{
                textAlign: 'left',
                padding: '16px 18px',
                border: '1px solid var(--border)',
                borderRadius: 12,
                background: 'rgba(255,255,255,0.03)',
                cursor: 'pointer',
                transition: 'all 0.15s ease',
              }}
              onMouseEnter={(e) => {
                const el = e.currentTarget;
                el.style.background = 'rgba(0,245,255,0.06)';
                el.style.borderColor = 'rgba(0,245,255,0.3)';
              }}
              onMouseLeave={(e) => {
                const el = e.currentTarget;
                el.style.background = 'rgba(255,255,255,0.03)';
                el.style.borderColor = 'var(--border)';
              }}
            >
              <div style={{ fontSize: 28, marginBottom: 8 }}>{tpl.emoji}</div>
              <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)', marginBottom: 4 }}>{tpl.name}</div>
              <div style={{ fontSize: 11, color: 'var(--muted)', lineHeight: 1.5 }}>{tpl.description}</div>
            </button>
          ))}
        </div>
      </div>
    </>
  );
}
